import { CaretRight as ChevronRight, House as Home } from '@phosphor-icons/react';
import { Link } from "@/lib/router-compat";

import { useCountry } from "@/hooks/useCountry";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import { buildBreadcrumbLd } from "@/utils/schemaMarkup";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Universal Breadcrumbs Component
 * SEO-optimized with structured data and internal linking
 * Fixes: "No outgoing links" and improves navigation
 */
export function Breadcrumbs({ items, className = "" }: BreadcrumbsProps) {
  const { country } = useCountry();
  const basePath = country === 'RO' ? '' : `/${country.toLowerCase()}`;
  
  // Home href should always be "/" for RO, or "/uk", "/de" for other countries
  const homeHref = basePath || '/';

  // Always start with home
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: country === 'RO' ? 'Acasă' : 'Home', href: homeHref },
    ...items
  ];

  // Generate JSON-LD structured data
  const structuredData = buildBreadcrumbLd(
    breadcrumbItems.map((item) => ({
      name: item.label,
      item: item.href && item.href !== '#' ? `https://roautomarket.ro${item.href}` : undefined,
    }))
  );

  return (
    <>
      {/* Structured Data for Google */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Visual Breadcrumbs */}
      <nav 
        aria-label="Breadcrumb" 
        className={`flex items-center gap-2 text-sm text-muted-foreground py-2 md:py-3 ${className}`}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isHome = index === 0;

          return (
            <div key={item.href} className="flex items-center gap-2">
              {index > 0 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
              )}
              
              {isLast ? (
                <span className="text-foreground font-medium" aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Link 
                  to={item.href}
                  className="hover:text-foreground transition-colors inline-flex items-center gap-1"
                >
                  {isHome && <Home className="h-4 w-4" />}
                  {item.label}
                </Link>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
}
